import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Loader2, ArrowLeft, ArrowRight, Search, Check, Plus, Plug } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConnectionProgressDrawer } from "@/components/ConnectionProgressDrawer";
import { SCHEMA } from "@/constants/testIds";
import api from "@/lib/api";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Database; return <C {...p} />; };

const STATUS = {
  available: { label: "Available", color: "#0FAE96" },
  beta: { label: "Beta", color: "#F59E0B" },
  soon: { label: "Coming soon", color: "#64748B" },
};

export function ConnectSourceDialog({ trigger, onConnected, workspaceId }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("gallery"); // gallery | form | select
  const [catalog, setCatalog] = useState([]);
  const [gallerySearch, setGallerySearch] = useState("");
  const [conn, setConn] = useState(null); // selected connector spec
  const [form, setForm] = useState({});
  const [name, setName] = useState("");
  const [listing, setListing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [jobId, setJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (open && catalog.length === 0) {
      api.get("/connectors/catalog")
        .then(({ data }) => setCatalog(data.connectors || []))
        .catch(() => toast.error("Could not load connectors"));
    }
  }, [open, catalog.length]);

  const reset = () => {
    setStep("gallery"); setConn(null); setForm({}); setName(""); setTables([]);
    setSelected(new Set()); setSearch(""); setGallerySearch("");
  };

  const pickConnector = (c) => {
    setConn(c);
    const init = {};
    c.fields.forEach((f) => { init[f.key] = f.key === "port" && c.default_port ? String(c.default_port) : ""; });
    setForm(init);
    setName(`${c.label} warehouse`);
    setStep("form");
  };

  const buildConfig = () => {
    const cfg = {};
    conn.fields.forEach((f) => {
      let v = form[f.key];
      if (v === "" || v == null) return;
      if (f.type === "number") v = Number(v);
      cfg[f.key] = v;
    });
    return cfg;
  };

  const onListTables = async (e) => {
    e.preventDefault();
    setListing(true);
    try {
      const { data } = await api.post("/sources/db/list-tables",
        { kind: conn.kind, config: buildConfig() },
        { params: workspaceId ? { workspace_id: workspaceId } : {} });
      const list = data.tables || [];
      setTables(list);
      setSelected(new Set(list.map((t) => t.fqn)));
      setStep("select");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not list tables");
    } finally {
      setListing(false);
    }
  };

  const toggle = (fqn) => setSelected((prev) => {
    const next = new Set(prev); next.has(fqn) ? next.delete(fqn) : next.add(fqn); return next;
  });

  const visible = tables.filter((t) => t.fqn.toLowerCase().includes(search.trim().toLowerCase()));
  const allVisibleSelected = visible.length > 0 && visible.every((t) => selected.has(t.fqn));
  const toggleAllVisible = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) visible.forEach((t) => next.delete(t.fqn));
    else visible.forEach((t) => next.add(t.fqn));
    return next;
  });
  const grouped = visible.reduce((acc, t) => { (acc[t.schema] = acc[t.schema] || []).push(t); return acc; }, {});

  const onConnect = async () => {
    if (selected.size === 0) { toast.error("Select at least one table to scan"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sources/db",
        { kind: conn.kind, name: name.trim() || conn.label, config: buildConfig(), tables: [...selected] },
        { params: workspaceId ? { workspace_id: workspaceId } : {} });
      setJobId(data.job_id);
      setOpen(false); reset(); setDrawerOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Connection failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onPipelineDone = (job) => {
    if (job?.status === "completed") { toast.success("Data source connected · glossary generated"); onConnected?.(); }
    else toast.error(job?.error || "Connection failed");
  };

  const busy = listing || submitting;
  const galleryVisible = catalog.filter((c) => c.label.toLowerCase().includes(gallerySearch.trim().toLowerCase()));
  const byCat = galleryVisible.reduce((a, c) => { (a[c.category] = a[c.category] || []).push(c); return a; }, {});

  return (
    <>
      <ConnectionProgressDrawer jobId={jobId} open={drawerOpen} onOpenChange={setDrawerOpen}
        onDone={onPipelineDone} title={`Connecting ${conn?.label || "data source"}`} />
      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) reset(); } }}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" data-testid="add-data-source-button"
              className="rounded-full border-border/80 text-xs uppercase tracking-[0.16em]">
              <Plus className="mr-2 h-4 w-4" /> Add data source
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="font-mono sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              {step !== "gallery" && conn ? <Icon name={conn.icon} className="h-5 w-5 text-accent" /> : <Plug className="h-5 w-5 text-accent" />}
              {step === "gallery" ? "Choose a data source"
                : step === "form" ? `Connect ${conn?.label}` : "Choose tables to scan"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {step === "gallery" ? "Connect any supported database. Use a read-only user — credentials are encrypted at rest."
                : step === "form" ? "Enter your connection details. We'll validate and list your tables before scanning anything."
                : "Select only the tables Sema should introspect and define. Skipping unused tables keeps a large warehouse fast."}
            </DialogDescription>
          </DialogHeader>

          {/* STEP 1: gallery */}
          {step === "gallery" && (
            <div data-testid="connector-gallery" className="mt-2 space-y-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="connector-gallery-search" value={gallerySearch}
                  onChange={(e) => setGallerySearch(e.target.value)} placeholder="Search databases…"
                  className="h-9 bg-card/60 pl-8 text-sm" />
              </div>
              <div className="max-h-[52vh] space-y-5 overflow-y-auto pr-1">
                {Object.entries(byCat).map(([cat, items]) => (
                  <div key={cat}>
                    <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{cat}</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {items.map((c) => {
                        const s = STATUS[c.status] || STATUS.soon;
                        return (
                          <button key={c.kind} type="button" data-testid={`connector-card-${c.kind}`}
                            onClick={() => pickConnector(c)}
                            className="group flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/40 px-4 py-3 text-left transition-colors hover:border-accent/50 hover:bg-accent/5">
                            <span className="flex items-center gap-3">
                              <Icon name={c.icon} className="h-5 w-5 text-muted-foreground group-hover:text-accent" />
                              <span className="text-sm font-medium">{c.label}</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.12em]" style={{ color: s.color }}>
                              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.color }} />{s.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: dynamic form */}
          {step === "form" && conn && (
            <form data-testid="connector-form" onSubmit={onListTables} className="mt-2 space-y-3">
              {conn.status === "soon" && (
                <p className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-[11px] text-yellow-600 dark:text-yellow-400">
                  This connector is rolling out soon. You can enter details now; if the driver isn't enabled yet, connecting will tell you.
                </p>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="ds-name" className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Connection name</Label>
                <Input id="ds-name" data-testid="connector-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="h-9 bg-card/60 text-sm" disabled={listing} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {conn.fields.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "col-span-2 space-y-1.5" : "col-span-1 space-y-1.5"}>
                    <Label htmlFor={`f-${f.key}`} className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <Textarea id={`f-${f.key}`} data-testid={`connector-field-${f.key}`} rows={3}
                        value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        placeholder={f.placeholder} className="bg-card/60 text-sm" disabled={listing} required={f.required} />
                    ) : (
                      <Input id={`f-${f.key}`} type={f.type} data-testid={`connector-field-${f.key}`}
                        value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        placeholder={f.placeholder} className="h-9 bg-card/60 text-sm" disabled={listing} required={f.required} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <Button type="button" variant="ghost" data-testid="connector-form-back" onClick={() => setStep("gallery")}
                  disabled={listing} className="h-10 rounded-full text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                </Button>
                <Button type="submit" disabled={listing} data-testid="connector-list-tables"
                  className="h-10 rounded-full bg-accent px-5 text-[11px] uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
                  {listing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Test &amp; list tables <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              </div>
            </form>
          )}

          {/* STEP 3: table selection */}
          {step === "select" && (
            <div data-testid={SCHEMA.pgStep2} className="mt-2 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid={SCHEMA.pgTableSearch} value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter tables…" className="h-9 bg-card/60 pl-8 text-sm" />
                </div>
                <Button type="button" variant="outline" data-testid={SCHEMA.pgSelectAll} onClick={toggleAllVisible}
                  className="h-9 shrink-0 rounded-full border-border/80 text-[11px] uppercase tracking-[0.14em]">
                  {allVisibleSelected ? "Clear" : "Select all"}
                </Button>
              </div>
              <div className="max-h-[42vh] space-y-3 overflow-y-auto rounded-lg border border-border/60 bg-background/40 p-3">
                {visible.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">No tables match “{search}”.</p>
                ) : (
                  Object.entries(grouped).map(([schema, schemaTables]) => (
                    <div key={schema}>
                      <p className="mb-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{schema} · {schemaTables.length}</p>
                      <div className="space-y-1">
                        {schemaTables.map((t) => {
                          const checked = selected.has(t.fqn);
                          return (
                            <button key={t.fqn} type="button" data-testid={SCHEMA.pgTableRow} onClick={() => toggle(t.fqn)}
                              className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                                checked ? "border-accent/50 bg-accent/10" : "border-border/50 hover:bg-muted/40"}`}>
                              <span className="flex items-center gap-2.5">
                                <span data-testid={SCHEMA.pgTableCheckbox}
                                  className={`flex h-4 w-4 items-center justify-center rounded border ${
                                    checked ? "border-accent bg-accent text-accent-foreground" : "border-border"}`}>
                                  {checked && <Check className="h-3 w-3" />}
                                </span>
                                <span className="font-medium">{t.table}</span>
                              </span>
                              <span className="text-[10px] text-muted-foreground">{t.columns} cols</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <Button type="button" variant="ghost" data-testid={SCHEMA.pgBack} onClick={() => setStep("form")}
                  disabled={submitting} className="h-10 rounded-full text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                </Button>
                <span data-testid={SCHEMA.pgSelectedCount} className="text-[11px] text-muted-foreground">
                  {selected.size} of {tables.length} selected
                </span>
                <Button type="button" onClick={onConnect} disabled={submitting || selected.size === 0}
                  data-testid={SCHEMA.pgConnectSelected}
                  className="h-10 rounded-full bg-accent text-[11px] uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Connect ${selected.size} table${selected.size === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
